/* eslint-disable react/display-name */
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '_constants/theme';

const { colors, devices, fontSizes, spaces } = theme;

const StyledBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;

  position: relative;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: ${`linear-gradient(180deg, ${colors.indigo[600]} 0%, ${colors.indigo[300]} 100%)`};
    border-bottom-left-radius: 100px;
    border-bottom-right-radius: 100px;
    z-index: -1;
  }

  @media ${devices.tablet} {
    &::before {
      border-bottom-left-radius: unset;
      border-bottom-right-radius: unset;
    }
  }
`;

const StyledBannerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: ${spaces.large24X * 10 + 80}px;
  width: 100%;

  @media ${devices.tablet} {
    flex-direction: column;
  }
`;

const StyledBannerLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 45%;
  position: relative;
  padding-right: ${spaces.large10X}px;
  justify-content: center;

  @media ${devices.tablet} {
    width: 100%;
    padding-top: ${spaces.large}px;
    padding-left: ${spaces.regular}px;
    padding-right: ${spaces.regular}px;
  }
`;
const StyledBannerRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-width: 55%;
  position: relative;
  justify-content: flex-end;

  @media ${devices.tablet} {
    width: 100%;
  }
`;
const StyledBackImageContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: end;
  z-index: -1;

  @media ${devices.tablet} {
    img {
      width: 100%;
    }
  }
`;

const StyledFrontImageContainer = styled(
  forwardRef(({ desktop, mobile, rightSpace, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  display: block;
  justify-content: center;
  align-items: flex-start;

  bottom: 0;
  right: 0;
  position: absolute;

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${devices.tablet} {
    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: flex;
        img {
          width: 100%;
        }
      `}
  }
`;

const StyledContentBlock = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin-bottom: ${spaces.medium}px;

  @media ${devices.tablet} {
    margin-bottom: ${spaces.small}px;
    button {
      width: 100%;
      justify-content: center;
      margin-bottom: ${spaces.small}px;
    }
  }
`;

const StyledTitle = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.large11X}px;
  line-height: 130%;
  color: ${colors.white};

  @media ${devices.tablet} {
    font-size: ${fontSizes.large8X}px;
    line-height: 140%;
  }
`;

const StyledText = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  color: ${colors.indigo[200]};
  margin-bottom: ${spaces.medium}px;

  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
    margin-bottom: ${spaces.small}px;
  }
`;

const ProductBannerSection = ({
  title,
  text,
  buttonsBlock,
  backgroundImage,
  desktopImage,
  mobileImage,
}) => {
  return (
    <StyledBanner>
      <StyledBannerContainer>
        <StyledBannerLeftColumn>
          <StyledContentBlock>
            <StyledTitle>{title}</StyledTitle>
          </StyledContentBlock>
          <StyledContentBlock>
            <StyledText>{text}</StyledText>
          </StyledContentBlock>
          <StyledContentBlock>{buttonsBlock}</StyledContentBlock>
        </StyledBannerLeftColumn>
        <StyledBannerRightColumn>
          <StyledBackImageContainer>
            <picture>
              <source srcSet={backgroundImage} type="image/svg+xml" />
              <img src={backgroundImage} alt="" />
            </picture>
          </StyledBackImageContainer>
          {desktopImage && (
            <StyledFrontImageContainer desktop>
              <picture>
                <source srcSet={desktopImage} type="image/png" />
                <img src={desktopImage} alt="" />
              </picture>
            </StyledFrontImageContainer>
          )}
          {mobileImage && (
            <StyledFrontImageContainer mobile>
              <picture>
                <source srcSet={mobileImage} type="image/png" />
                <img src={mobileImage} alt="" />
              </picture>
            </StyledFrontImageContainer>
          )}
        </StyledBannerRightColumn>
      </StyledBannerContainer>
    </StyledBanner>
  );
};

export default ProductBannerSection;
